import Box from '@mui/material/Box';
import { css } from '@mui/material/styles';
import { useIntl } from '@doxyme/locale';
import { modalClasses } from '../modal.classes';
import {
  type ModalFooterStyles,
  useModalFooterStyles,
} from './modalFooter.styles';
import { type NotificationLevel } from '../../status';
import { Button, type ButtonProps } from '../../buttons/button';
import { Icon } from '@doxyme/icons';
import { type CSSProperties, type JSX } from 'react';
import { Tooltip } from '../../tooltip';
import { Caption } from '../../../../typography';

type ModalFooterButton = {
  status?: NotificationLevel;
  tooltipText?: string;
} & Partial<ButtonProps>;

export type ModalFooterProps = {
  onBack?: () => void;
  styles?: Partial<
    ModalFooterStyles<CSSProperties> & {
      backIcon: CSSProperties;
    }
  >;
  submitButton?: ModalFooterButton;
  secondaryButton?: ModalFooterButton;
  layout?: 'default' | 'equal-buttons';
  caption?: string;
};

type ButtonWithTooltipProps = {
  button?: ModalFooterButton;
  children: JSX.Element;
};

const ButtonWithTooltip = ({ button, children }: ButtonWithTooltipProps) => {
  const defaultStyles = useModalFooterStyles({
    layout: 'default',
  });

  if (button === undefined) return undefined;
  if (button.tooltipText === undefined) return children;

  if (button.isDisabled) {
    return (
      <Tooltip title={button.tooltipText}>
        <Box css={defaultStyles.buttonContainer}>{children}</Box>
      </Tooltip>
    );
  }
  return <Tooltip title={button.tooltipText}>{children}</Tooltip>;
};

/**
 * Renders a fixed bottom Footer for the Modal. Optionally pass in `submitButton` when building a form. Has to be placed within `<ModalContent>`
 */
export const ModalFooter = ({
  onBack,
  submitButton,
  secondaryButton,
  styles,
  layout = 'default',
  caption,
}: ModalFooterProps): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const defaultStyles = useModalFooterStyles({
    layout,
  });

  return (
    <Box css={css(defaultStyles.container, modalClasses.footer)}>
      <Box css={defaultStyles.buttonContainer}>
        {onBack && (
          <Tooltip title={formatMessage({ id: 'back' })} offset={-4}>
            <span>
              <Button
                type='outline'
                className={modalClasses.backButton}
                onClick={onBack}
                width={40}
                css={defaultStyles.backButton}
                aria-label={formatMessage({ id: 'back' })}
              >
                <Icon.ArrowLeft
                  type='filled'
                  className={modalClasses.backIcon}
                  style={styles?.backIcon}
                  size='small'
                  aria-hidden
                />
              </Button>
            </span>
          </Tooltip>
        )}
        <Box css={defaultStyles.buttons}>
          {layout === 'equal-buttons' ? (
            <>
              <ButtonWithTooltip button={submitButton}>
                <Button
                  {...submitButton}
                  status={submitButton?.status}
                  type={submitButton?.type ?? 'primary'}
                />
              </ButtonWithTooltip>
              <ButtonWithTooltip button={secondaryButton}>
                <Button
                  {...secondaryButton}
                  type={secondaryButton?.type ?? 'secondary'}
                />
              </ButtonWithTooltip>
            </>
          ) : (
            <>
              <ButtonWithTooltip button={secondaryButton}>
                <Button
                  {...secondaryButton}
                  type={secondaryButton?.type ?? 'secondary'}
                />
              </ButtonWithTooltip>
              <ButtonWithTooltip button={submitButton}>
                <Button
                  {...submitButton}
                  status={submitButton?.status}
                  type={submitButton?.type ?? 'primary'}
                />
              </ButtonWithTooltip>
            </>
          )}
        </Box>
      </Box>
      {caption && <Caption css={defaultStyles.caption}>{caption}</Caption>}
    </Box>
  );
};
